/**
 * Duplicating and deleting a whole workout — the two structural acts the card's ⋯ dispatches, and
 * the one of them that has to be confirmed first.
 *
 * ── Why the confirm lives here and not in the card ───────────────────────────────────────────
 * A dialog raised from inside a card would mount once per card, and the seven of them on a week
 * would be seven `AlertDialog` roots competing for the same portal and the same focus trap. There is
 * exactly one pending deletion on a screen at a time, so there is one prompt, held by the screen, and
 * the card only asks for it.
 *
 * ── The copy names the CONSEQUENCE, and a running fork's is the phone's sentence PLUS ours ───
 * A running fork's day disappears from every week the client has not reached while everything they
 * already trained stays in their history, and THAT sentence is the shared locale's
 * `plans.builder.deleteDayConfirmBodyLive` — the phone's own words, so a trainer who has read the
 * consequence on one platform reads the same one here. Its `…Cycle` twin is the self-paced wording.
 *
 * It is not the whole answer, though, and until 16 September 2026 it was all a live plan got. Two
 * facts this app HAS were dropped exactly where they matter most: that the day takes its exercises
 * with it, and that the edit is undoable until the plan is saved — the phone has no undo, so its
 * sentence cannot carry one. Both are appended, from the same two web keys the template path uses,
 * so the two prompts cannot drift. The phase delete one file over is already worded this way.
 *
 * The name is captured at REQUEST time rather than read back at confirm time. Between the two the
 * trainer can undo, rename or delete something else, and a prompt that re-read the tree would name
 * whatever now stands there.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { duplicateDay, removeDay, useIsForkPlan, usePlanSchedulingMode } from '~/entities/plan-draft';
import type { ConfirmPrompt } from '~/shared/ui/ConfirmDialog';

import { KEY } from '../model/builderCopy';

/** One day, as the prompt has to remember it while the trainer answers. */
interface PendingDeletion {
  readonly slotToken: string;
  readonly name: string;
}

export interface DayActions {
  /** Copy a day and everything in it, then say so in the canvas's live region. */
  readonly duplicate: (slotToken: string, name: string) => void;
  /** Raise the delete confirm for one day. Nothing is written until it is answered. */
  readonly requestDelete: (slotToken: string, name: string) => void;
  /** The prompt the screen renders, or `null` when nothing is waiting on an answer. */
  readonly prompt: ConfirmPrompt | null;
  readonly confirmDelete: () => void;
  readonly cancelDelete: () => void;
}

/**
 * @param announce What the canvas's live region says after an act completes. A deletion removes the
 *   card the trainer was standing on, so without it the screen changes under a screen-reader user
 *   with nothing said; a duplication adds one somewhere they were not looking.
 * @param restoreFocus Where focus goes after a confirmed deletion. Base UI hands focus back to the
 *   control that opened the dialog, and after a delete that control — the card's ⋯ — no longer
 *   exists, so focus would land on `<body>` and the next Tab would restart at the top of the
 *   document. The canvas answers with its own panel, which is where the change happened.
 */
export function useDayActions(
  announce: (message: string) => void,
  restoreFocus: () => void,
): DayActions {
  const { t } = useTranslation();
  const isFork = useIsForkPlan();
  const isSelfPaced = usePlanSchedulingMode() === 'selfpaced';
  const [pending, setPending] = useState<PendingDeletion | null>(null);

  return {
    duplicate: (slotToken, name) => {
      duplicateDay(slotToken);
      announce(t(`${KEY}.dayActions.duplicated`, { name }));
    },
    requestDelete: (slotToken, name) => {
      setPending({ slotToken, name });
    },
    prompt:
      pending === null
        ? null
        : {
            title: t(`${KEY}.dayActions.deleteTitle`, { name: pending.name }),
            description: [
              // The shared sentence FIRST on a running fork, because that is the consequence a
              // trainer has already met on the phone and the one this app cannot reword.
              isFork ? t(`plans.builder.deleteDayConfirmBodyLive${isSelfPaced ? 'Cycle' : ''}`) : null,
              t(`${KEY}.dayActions.deleteScope`),
              t(`${KEY}.undoHint`),
            ]
              .filter((sentence) => sentence !== null)
              .join(' '),
            confirmLabel: t(`${KEY}.dayActions.deleteConfirm`),
            cancelLabel: t(`${KEY}.dayActions.cancel`),
            // The confirm destroys the day and there is no answer here that keeps it, so it is
            // drawn in `--danger`. Cancel stays neutral, and stays the one focus lands on.
            destructiveConfirm: true,
          },
    confirmDelete: () => {
      if (pending === null) return;
      removeDay(pending.slotToken);
      announce(t(`${KEY}.dayActions.deleted`, { name: pending.name }));
      setPending(null);
      restoreFocus();
    },
    cancelDelete: () => {
      setPending(null);
    },
  };
}
